import { useRef, useState, type DragEvent, type ReactNode } from "react";
import type { ComposeInput, Target } from "@easyshop/shared";
import { TASK_PRIORITIES, TASK_PRIORITY_LABELS, TASK_TYPES, TASK_TYPE_LABELS } from "@easyshop/shared";
import { Drawer, DrawerHeader, FieldLabel, cx, inputClass } from "@/components/ui";
import { useSession } from "@/hooks/useSession";
import { useUi, type ComposeDefaults } from "@/hooks/useUi";
import { BoutiquePicker } from "@/components/BoutiquePicker";
import { uploadFiles, useCompose, useGroups } from "@/lib/queries";

// --- Destinataires (partagé avec le formulaire de campagne) ---------------------------

export type TargetMode = "boutique" | "plusieurs" | "groupe" | "reseau";
export interface TargetState {
  mode: TargetMode;
  boutiqueIds: string[];
  groupId: string;
}

export function initialTarget(boutiqueIds: string[] | undefined, allIds: string[]): TargetState {
  const ids = boutiqueIds?.filter(Boolean) ?? [];
  if (ids.length > 1) return { mode: "plusieurs", boutiqueIds: ids, groupId: "" };
  if (ids.length === 1) return { mode: "boutique", boutiqueIds: ids, groupId: "" };
  if (allIds.length === 1) return { mode: "boutique", boutiqueIds: allIds, groupId: "" };
  return { mode: "boutique", boutiqueIds: [], groupId: "" };
}

/** Convertit l'état du sélecteur en `Target` de l'API, ou renvoie un message d'erreur. */
export function toTarget(t: TargetState): Target | string {
  if (t.mode === "reseau") return { kind: "network" };
  if (t.mode === "groupe") return t.groupId ? { kind: "group", groupId: t.groupId } : "Choisir un groupe";
  return t.boutiqueIds.length ? { kind: "boutiques", boutiqueIds: t.boutiqueIds } : "Choisir au moins une boutique";
}

/** « DESTINATAIRE » : Une boutique / Plusieurs / Un groupe / Tout le réseau (+ liste déroulante des boutiques ou groupe). */
export function TargetPicker({ value, onChange, label = "Destinataire" }: { value: TargetState; onChange: (t: TargetState) => void; label?: string }) {
  const { me, isManagerView } = useSession();
  const groups = useGroups();
  const boutiques = me.boutiques;
  const options: { key: TargetMode; label: string; show: boolean }[] = [
    { key: "boutique", label: "Une boutique", show: true },
    { key: "plusieurs", label: "Plusieurs", show: boutiques.length > 1 },
    { key: "groupe", label: "Un groupe", show: (groups.data?.length ?? 0) > 0 },
    { key: "reseau", label: isManagerView ? "Toutes mes boutiques" : "Tout le réseau", show: true },
  ];

  const setMode = (mode: TargetMode) => {
    const ids = mode === "boutique" ? value.boutiqueIds.slice(0, 1) : value.boutiqueIds;
    onChange({ ...value, mode, boutiqueIds: ids, groupId: value.groupId || groups.data?.[0]?.id || "" });
  };

  return (
    <>
      <div>
        <div className="mb-2 text-[11.5px] font-bold uppercase text-muted-2">{label}</div>
        <div className="flex flex-wrap gap-[6px]">
          {options
            .filter((o) => o.show)
            .map((o) => (
              <button
                key={o.key}
                type="button"
                onClick={() => setMode(o.key)}
                className={cx(
                  "rounded-full border border-line px-3 py-[7px] text-[12px] font-semibold",
                  value.mode === o.key ? "bg-accent text-white" : "bg-white text-[oklch(0.5_0.012_150)]"
                )}
              >
                {o.label}
              </button>
            ))}
        </div>
      </div>

      {(value.mode === "boutique" || value.mode === "plusieurs") && (
        <BoutiquePicker
          boutiques={boutiques}
          value={value.boutiqueIds}
          multiple={value.mode === "plusieurs"}
          placeholder={value.mode === "plusieurs" ? "Choisir les boutiques…" : "Choisir une boutique…"}
          onChange={(ids) => onChange({ ...value, boutiqueIds: ids })}
        />
      )}

      {value.mode === "groupe" && (
        <select value={value.groupId} onChange={(e) => onChange({ ...value, groupId: e.target.value })} className={inputClass}>
          {(groups.data ?? []).map((g) => (
            <option key={g.id} value={g.id}>
              {g.name} ({g.boutiqueIds.length})
            </option>
          ))}
        </select>
      )}
    </>
  );
}

/** Zone pointillée « Glisser une image ou cliquer… » (glisser-déposer + clic). */
export function DropZone({
  files,
  onFiles,
  multiple = true,
  label = "Glisser une image ou cliquer pour joindre un visuel",
}: {
  files: File[];
  onFiles: (f: File[]) => void;
  multiple?: boolean;
  label?: ReactNode;
}) {
  const ref = useRef<HTMLInputElement>(null);
  const [over, setOver] = useState(false);
  const onDrop = (e: DragEvent) => {
    e.preventDefault();
    setOver(false);
    const dropped = [...e.dataTransfer.files];
    if (dropped.length) onFiles(multiple ? [...files, ...dropped].slice(0, 10) : dropped.slice(0, 1));
  };
  return (
    <div>
      <div
        role="button"
        tabIndex={0}
        onClick={() => ref.current?.click()}
        onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && ref.current?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          setOver(true);
        }}
        onDragLeave={() => setOver(false)}
        onDrop={onDrop}
        className={cx(
          "cursor-pointer rounded-[12px] border-[1.5px] border-dashed p-4 text-center text-[12px] text-muted-2",
          over ? "border-accent bg-accent-soft" : "border-[oklch(0.85_0.01_150)]"
        )}
      >
        {label}
      </div>
      <input
        ref={ref}
        type="file"
        accept="image/*,application/pdf"
        multiple={multiple}
        className="hidden"
        onChange={(e) => {
          const picked = [...(e.target.files ?? [])];
          if (picked.length) onFiles(multiple ? [...files, ...picked].slice(0, 10) : picked.slice(0, 1));
          e.target.value = "";
        }}
      />
      {files.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-[6px]">
          {files.map((f, i) => (
            <span key={`${f.name}-${i}`} className="inline-flex max-w-full items-center gap-[6px] rounded-full bg-page px-[10px] py-1 text-[11.5px] font-semibold">
              <span className="truncate">{f.name}</span>
              <button
                type="button"
                aria-label={`Retirer ${f.name}`}
                onClick={() => onFiles(files.filter((_, j) => j !== i))}
                className="text-muted-2 hover:text-danger"
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

// --- Tiroir « Nouveau message / tâche / annonce » ---------------------------------------

type Kind = "message" | "tache" | "annonce";
type TaskType = (typeof TASK_TYPES)[number];
type Priority = (typeof TASK_PRIORITIES)[number];

const TITLES: Record<Kind, string> = { message: "Nouveau message", tache: "Nouvelle tâche", annonce: "Nouvelle annonce" };

export function ComposeDrawer() {
  const { compose, closeCompose } = useUi();
  return (
    <Drawer open={compose != null} onClose={closeCompose}>
      {compose && <ComposeForm key={JSON.stringify(compose)} defaults={compose} onClose={closeCompose} />}
    </Drawer>
  );
}

function ComposeForm({ defaults, onClose }: { defaults: ComposeDefaults; onClose: () => void }) {
  const { me, can } = useSession();
  const composeMut = useCompose();
  const kinds: { key: Kind; label: string }[] = [
    { key: "message" as const, label: "Message" },
    ...(can("tasks.create") ? [{ key: "tache" as const, label: "Tâche" }] : []),
    ...(can("announcements.send") ? [{ key: "annonce" as const, label: "Annonce" }] : []),
  ];
  const [kind, setKind] = useState<Kind>(kinds.some((k) => k.key === defaults.kind) ? defaults.kind! : "message");
  const [target, setTarget] = useState<TargetState>(() => initialTarget(defaults.boutiqueIds, me.boutiques.map((b) => b.id)));
  const [title, setTitle] = useState(defaults.title ?? "");
  const [description, setDescription] = useState(defaults.description ?? "");
  const [type, setType] = useState<TaskType>("FREE");
  const [priority, setPriority] = useState<Priority>("NORMAL");
  const [dueAt, setDueAt] = useState("");
  const [requiresProof, setRequiresProof] = useState(false);
  const [requiresInvoice, setRequiresInvoice] = useState(false);
  const [files, setFiles] = useState<File[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setError(null);
    const t = toTarget(target);
    if (typeof t === "string") return setError(t);
    const cleanTitle = title.trim();
    const body = description.trim();
    if (kind !== "message" && !cleanTitle) return setError("Titre requis");
    if (kind === "message" && !cleanTitle && !body && files.length === 0) return setError("Message vide");
    setBusy(true);
    try {
      const attachmentIds = files.length ? (await uploadFiles(files)).map((a) => a.id) : [];
      let input: ComposeInput;
      if (kind === "tache") {
        input = {
          kind,
          target: t,
          title: cleanTitle,
          description: body || null,
          type,
          priority,
          dueAt: dueAt ? new Date(`${dueAt}T23:59:00`).toISOString() : null,
          requiresProof,
          requiresInvoice,
          attachmentIds,
        } as ComposeInput;
      } else {
        input = { kind, target: t, title: cleanTitle, body, attachmentIds };
      }
      await composeMut.mutateAsync(input);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Envoi impossible");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      className="flex min-h-full flex-1 flex-col gap-4"
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
    >
      <DrawerHeader title={TITLES[kind]} onClose={onClose} />

      {kinds.length > 1 && (
        <div className="flex gap-[6px] rounded-full bg-page p-1">
          {kinds.map((k) => (
            <button
              key={k.key}
              type="button"
              onClick={() => setKind(k.key)}
              className={cx(
                "flex-1 rounded-full py-2 text-center text-[12.5px] font-semibold",
                kind === k.key ? "bg-white text-[oklch(0.3_0.05_155)]" : "bg-transparent text-[oklch(0.5_0.012_150)]"
              )}
            >
              {k.label}
            </button>
          ))}
        </div>
      )}

      <TargetPicker value={target} onChange={setTarget} />

      <div>
        <FieldLabel>Titre</FieldLabel>
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          maxLength={200}
          placeholder="Ex. Mettre en avant le Miel de Provence"
          className={inputClass}
        />
      </div>

      {kind === "tache" && (
        <>
          <button
            type="button"
            onClick={() => {
              setType("ARRIVAL");
              setTitle("Créer l'arrivage");
              setRequiresInvoice(true);
              setRequiresProof(false);
            }}
            className={cx(
              "rounded-[10px] border px-3 py-2 text-left text-[12.5px] font-semibold",
              type === "ARRIVAL" ? "border-accent bg-accent-soft text-accent" : "border-line bg-white text-[oklch(0.4_0.02_150)]"
            )}
          >
            📦 Tâche par défaut : « Créer l'arrivage » (facture à joindre)
          </button>
          <div>
            <FieldLabel>Type de tâche</FieldLabel>
            <select
              value={type}
              onChange={(e) => {
                const t = e.target.value as TaskType;
                setType(t);
                setRequiresInvoice(t === "ARRIVAL");
              }}
              className={inputClass}
            >
              {TASK_TYPES.map((k) => (
                <option key={k} value={k}>
                  {TASK_TYPE_LABELS[k]}
                </option>
              ))}
            </select>
          </div>
          <div className="flex gap-[10px]">
            <div className="min-w-0 flex-1">
              <FieldLabel>Échéance</FieldLabel>
              <input type="date" value={dueAt} onChange={(e) => setDueAt(e.target.value)} className={cx(inputClass, "px-[10px] py-[9px]")} />
            </div>
            <div className="min-w-0 flex-1">
              <FieldLabel>Priorité</FieldLabel>
              <div className="flex gap-1">
                {TASK_PRIORITIES.map((p) => (
                  <button
                    key={p}
                    type="button"
                    onClick={() => setPriority(p)}
                    className={cx(
                      "flex-1 rounded-[8px] py-2 text-center text-[11px] font-semibold",
                      priority === p ? "bg-accent text-white" : "bg-page text-[oklch(0.5_0.012_150)]"
                    )}
                  >
                    {TASK_PRIORITY_LABELS[p]}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <label className="flex cursor-pointer items-center gap-2">
            <input type="checkbox" checked={requiresProof} onChange={(e) => setRequiresProof(e.target.checked)} className="h-4 w-4 accent-[oklch(0.4_0.08_155)]" />
            <span className="text-[12.5px]">Photo de preuve requise à la complétion</span>
          </label>
          <label className="flex cursor-pointer items-center gap-2">
            <input type="checkbox" checked={requiresInvoice} onChange={(e) => setRequiresInvoice(e.target.checked)} className="h-4 w-4 accent-[oklch(0.4_0.08_155)]" />
            <span className="text-[12.5px]">Facture à joindre avant de terminer</span>
          </label>
        </>
      )}

      <div>
        <FieldLabel>Description</FieldLabel>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder={kind === "tache" ? "Détails pour le gérant…" : "Votre message…"}
          className={cx(inputClass, "min-h-[70px] resize-y")}
        />
      </div>

      <DropZone files={files} onFiles={setFiles} />

      {error && <div className="rounded-[10px] bg-danger-bg px-3 py-2 text-[12.5px] font-semibold text-danger">{error}</div>}

      <div className="flex-1" />
      <div className="flex gap-[10px]">
        <button type="button" onClick={onClose} className="flex-1 rounded-full border border-line py-[11px] text-center text-[13px] font-bold">
          Annuler
        </button>
        <button
          type="submit"
          disabled={busy}
          className="flex-1 rounded-full bg-accent py-[11px] text-center text-[13px] font-bold text-white hover:bg-accent-strong disabled:opacity-60"
        >
          {busy ? "Envoi…" : "Envoyer"}
        </button>
      </div>
    </form>
  );
}
